import type { Accessor, Setter, VoidComponent } from "solid-js";
import { createContext, createMemo, createSignal, Match, Show, Switch } from "solid-js";
import FormEditorDragDropProvider from "./FormEditorDragDropProvider";
import FormEditorHeader from "./FormEditorHeader";
import EditorContent from "./editor/EditorContent";
import RulesContent from "./rules/RulesContent";
import IntegrationsContent from "./integrations/IntegrationsContent";
import SettingsContent from "./settings/SettingsContent";
import { Navigate, useLocation } from "@solidjs/router";
import FormVersionRestore from "./FormVersionRestore";
import { makeEventListener } from "@solid-primitives/event-listener";
import FormVersionRestoreService from "../../services/FormVersionRestoreService";
import FormVersionService from "../../services/FormVersionService";
import SaveNotAllowedModal from "./header/SaveNotAllowedModal";
import { useTranslation } from "../../state/i18n";
import SubformsContent from "./subforms/SubformsContent";
import SubformDrawer from "./subforms/SubformDrawer";
import VariablesContent from "./variables/VariablesContent";
import { useModalManager } from "../../components/modal/ModalManager";
import { initializedContext } from "../../utils/initializedUtil";
import type { SidePanelSelection } from "../../components/panels/FormEditorSidePanel";
import { FieldEditorSidePanel, FormDetailsSidePanel } from "../../components/panels/FormEditorSidePanel";
import FormService from "../../services/FormService";
import { narrow } from "../../utils/solidUtils";
import { SideNavContent } from "./sideNav/SideNavContent";
import VariableService from "../../services/VariableService";
import VersionHistoryDrawer from "./history/VersionHistoryDrawer.tsx";

export type FormEditorSidePanelState = {
  selectedPanel: Accessor<SidePanelSelection | null>;
  setSelectedPanel: Setter<SidePanelSelection | null>;
};
export const FormEditorSidePanelContext = createContext<FormEditorSidePanelState>();
export const useFormEditorSidePanel = () =>
  initializedContext(FormEditorSidePanelContext, "useFormEditorSidePanel", "FormEditorSidePanelContext");

export enum FormEditorRoute {
  FIELDS = "fields",
  SUBFORMS = "subforms",
  RULES = "rules",
  INTEGRATIONS = "integrations",
  SETTINGS = "settings",
  VARIABLES = "variables",
}

const FormEditorContent: VoidComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { open } = useModalManager();
  const menuItem = createMemo<FormEditorRoute>(() => location.pathname.split("/").at(-1)! as FormEditorRoute);
  let contentContainerRef!: HTMLDivElement;
  const [selectedPanel, setSelectedPanel] = createSignal<SidePanelSelection | null>(null);

  makeEventListener(window, "beforeunload", (event) => {
    if (!FormVersionService.hasUpdated()) {
      return;
    }
    event.preventDefault();
  });

  makeEventListener(window, "pagehide", () => FormVersionRestoreService.markCleanExit());

  makeEventListener(window, "pageshow", (event) => {
    if (event.persisted) {
      FormVersionRestoreService.clearCleanExit();
    }
  });

  makeEventListener(window, "focus", () => {
    if (!FormVersionService.isDraft()) {
      open({
        content: () => (
          <SaveNotAllowedModal
            title={t(($) => $.FORM_EDITOR_SAVE_NOT_ALLOWED_TITLE)}
            description={t(($) => $.FORM_EDITOR_SAVE_NOT_ALLOWED_DESCRIPTION)}
          />
        ),
      });
    }
  });

  const form = FormService.getForm();

  return (
    <FormEditorSidePanelContext.Provider value={{ selectedPanel, setSelectedPanel }}>
      <FormEditorDragDropProvider>
        <div class="flex h-screen min-h-20 min-w-desktop flex-col scroll-smooth bg-default">
          <FormEditorHeader activeItem={menuItem()} />
          <div class="flex flex-1 overflow-hidden bg-surface-extra-subtle">
            <SideNavContent activeItem={menuItem()} />
            <main
              class="relative flex flex-1 flex-col items-center overflow-y-auto px-8 py-15 -outline-offset-1 focus-visible:outline-default"
              ref={contentContainerRef}
            >
              <div class="flex w-full max-w-182 flex-col">
                <Switch>
                  <Match when={menuItem() === FormEditorRoute.FIELDS}>
                    <EditorContent
                      contentContainer={contentContainerRef}
                      header={{ title: form.meta.name, description: form.meta.description }}
                    />
                  </Match>
                  <Match when={menuItem() === FormEditorRoute.SUBFORMS}>
                    <SubformsContent />
                  </Match>
                  <Match when={menuItem() === FormEditorRoute.RULES}>
                    <RulesContent />
                  </Match>
                  <Match when={menuItem() === FormEditorRoute.INTEGRATIONS}>
                    <IntegrationsContent contentContainer={contentContainerRef} />
                  </Match>
                  <Match when={menuItem() === FormEditorRoute.SETTINGS}>
                    <SettingsContent />
                  </Match>
                  <Match when={menuItem() === FormEditorRoute.VARIABLES}>
                    <Show when={VariableService.showVariables()} fallback={<Navigate href="../fields" />}>
                      <VariablesContent />
                    </Show>
                  </Match>
                </Switch>
              </div>
            </main>
            <Show when={menuItem() === FormEditorRoute.FIELDS}>
              <Switch>
                <Match when={narrow(selectedPanel(), (panel) => panel.type === "field")}>
                  {(panel) => <FieldEditorSidePanel {...panel()} />}
                </Match>
                <Match when={narrow(selectedPanel(), (panel) => panel.type === "formDetails")}>
                  <FormDetailsSidePanel />
                </Match>
              </Switch>
            </Show>
          </div>
        </div>
        <SubformDrawer />
        <VersionHistoryDrawer />
        <FormVersionRestore />
      </FormEditorDragDropProvider>
    </FormEditorSidePanelContext.Provider>
  );
};

export default FormEditorContent;
